import { useEffect, useRef, useState } from 'react'
import { acceptInvite, checkInvite, guessDeviceName } from '../lib/api'
import { useStore } from '../state/store'
import { Logo } from '../components/Logo'

/**
 * What someone sees when they open an invitation link.
 *
 * They choose their own password here, which is the whole reason invitations
 * exist: no secret travels through email or chat, and the person who invited
 * them never learns it.
 */
export function AcceptInviteView({ token, onDone }: { token: string; onDone: () => void }) {
  const { dispatch } = useStore()

  const [state, setState] = useState<'checking' | 'ready' | 'unusable'>('checking')
  const [invite, setInvite] = useState<{ email: string; name: string } | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [show, setShow] = useState(false)
  const [deviceName, setDeviceName] = useState(guessDeviceName())
  const [busy, setBusy] = useState(false)
  const passwordRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    void checkInvite(token).then((result) => {
      if (result.ok) {
        setInvite({ email: result.email, name: result.name })
        setState('ready')
        passwordRef.current?.focus()
      } else {
        setMessage(result.error)
        setState('unusable')
      }
    })
  }, [token])

  const tooShort = password.length > 0 && password.length < 12
  const mismatch = confirm.length > 0 && confirm !== password
  const canSubmit = password.length >= 12 && confirm === password && !busy

  const submit = async (event?: React.FormEvent) => {
    event?.preventDefault()
    if (!canSubmit) return

    setBusy(true)
    setMessage(null)
    const result = await acceptInvite(token, password, deviceName)
    setBusy(false)

    if (!result.ok) {
      setMessage(result.error)
      return
    }

    try {
      localStorage.setItem('language-island:last-email', result.user.email)
    } catch {
      // Storage refusal must not block a successful sign-up.
    }

    dispatch({
      type: 'patchSync',
      patch: {
        token: result.token,
        deviceId: result.deviceId,
        deviceName: result.deviceName,
        accountEmail: result.user.email,
        lastError: null,
        cursor: 0,
        lastPushedAt: 0,
        // Nothing may be pushed at this account before it is pulled.
        lastSyncAt: null,
      },
    })
    onDone()
  }

  return (
    <div className="signin">
      <div className="signin-head">
        <Logo size={44} />
      </div>

      {state === 'checking' && <p className="lede">Checking your invitation…</p>}

      {state === 'unusable' && (
        <>
          <h1 className="page-title" style={{ marginBottom: '0.3rem' }}>
            This link no longer works
          </h1>
          <div className="notice is-error" style={{ marginBottom: '1rem' }}>
            {message}
          </div>
          <button type="button" className="btn" onClick={onDone}>
            Go to the app
          </button>
        </>
      )}

      {state === 'ready' && invite && (
        <>
          <h1 className="page-title" style={{ marginBottom: '0.3rem' }}>
            {invite.name ? `Welcome, ${invite.name}` : 'Welcome'}
          </h1>
          <p className="lede" style={{ marginBottom: '1.4rem' }}>
            Choose a password for <strong>{invite.email}</strong>. Nobody else sees it — not even
            whoever invited you.
          </p>

          {message && (
            <div className="notice is-error" style={{ marginBottom: '1rem' }}>
              {message}
            </div>
          )}

          <form className="card" onSubmit={submit}>
            {/* Carries the account's identity for password managers, which will
                not offer to save a credential with no username attached. */}
            <input
              type="email"
              name="email"
              autoComplete="username"
              value={invite.email}
              readOnly
              hidden
            />

            <label className="field">
              <span className="field-label">Choose a password</span>
              <input
                ref={passwordRef}
                className="input is-data"
                type={show ? 'text' : 'password'}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>

            <label className="field">
              <span className="field-label">Type it again</span>
              <input
                className="input is-data"
                type={show ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </label>

            <div className="row">
              <label className="checkline">
                <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
                <span className="muted" style={{ fontSize: '0.86rem' }}>
                  Show password
                </span>
              </label>
            </div>

            <p
              className="muted"
              style={{ fontSize: '0.84rem', margin: '0.2rem 0 0.6rem' }}
              aria-live="polite"
            >
              {tooShort
                ? `At least 12 characters — ${12 - password.length} to go.`
                : mismatch
                  ? 'The two entries do not match yet.'
                  : 'At least 12 characters. Three or four unrelated words beats one clever word.'}
            </p>

            <details className="signin-more">
              <summary>Name this device — {deviceName}</summary>
              <input
                className="input"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                style={{ marginTop: '0.5rem' }}
              />
            </details>

            <div className="row" style={{ marginTop: '1rem' }}>
              <button type="submit" className="btn is-primary" disabled={!canSubmit}>
                {busy ? 'Setting up…' : 'Create my account'}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  )
}
